import React, { useState, useEffect } from 'react';
import { EditIcon, PlusIcon, CloseIcon, SettingsIcon, TrashIcon, LightbulbIcon } from './Icons';
import CustomDropdown from './CustomDropdown';

function ToolEditorDialog({ tool, onSave, onClose }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [parameters, setParameters] = useState([]);
  const [code, setCode] = useState('');

  useEffect(() => {
    if (tool) {
      setName(tool.name || '');
      setDescription(tool.description || '');
      setParameters(tool.parameters || []);
      setCode(tool.code || '');
    } else {
      setName('my_tool');
      setDescription('A custom tool');
      setParameters([
        { name: 'input_text', type: 'string', description: 'Input text', required: true }
      ]);
      setCode(`# Access parameters via function arguments
result = f"Processed: {input_text}"
return {"status": "success", "result": result}`);
    }
  }, [tool]);

  const addParameter = () => {
    setParameters([...parameters, {
      name: 'new_param',
      type: 'string',
      description: '',
      required: false
    }]);
  };

  const updateParameter = (index, field, value) => {
    const updated = [...parameters];
    updated[index] = { ...updated[index], [field]: value };
    setParameters(updated);
  };

  const deleteParameter = (index) => {
    setParameters(parameters.filter((_, i) => i !== index));
  };

  const handleSave = () => {
    if (!name.trim()) {
      console.warn('Tool name is required');
      return;
    }

    if (!/^[a-zA-Z_][a-zA-Z0-9_]*$/.test(name)) {
      console.warn('Tool name must be a valid Python identifier');
      return;
    }

    const toolData = {
      id: tool ? tool.id : `custom_${name}_${Date.now()}`,
      name: name.trim(),
      description: description.trim(),
      parameters,
      code: code.trim(),
      type: 'custom'
    };

    onSave(toolData);
  };

  return (
    <div className="modal-overlay">
      <div className="modal modal-lg">
        {/* Header */}
        <div className="modal-header">
          <h3 className="modal-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {tool ? <EditIcon size={16} color="#60D2CC" /> : <PlusIcon size={16} color="#60D2CC" />}
            {tool ? 'Edit Custom Tool' : 'Create Custom Tool'}
          </h3>
          <button className="modal-close" onClick={onClose} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CloseIcon size={14} />
          </button>
        </div>

        {/* Body */}
        <div className="modal-body">
          <div className="input-group">
            <label className="input-label">Tool Name *</label>
            <input
              type="text"
              className="input font-mono"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my_custom_tool"
            />
            <div className="input-hint">
              Must be a valid Python identifier (e.g., search_web, calculate_sum)
            </div>
          </div>

          <div className="input-group">
            <label className="input-label">Description</label>
            <textarea
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Describe what this tool does..."
              rows={2}
            />
            <div className="input-hint">
              Helps the AI understand when to use this tool
            </div>
          </div>

          {/* Parameters Section */}
          <div style={{ 
            marginTop: 'var(--spacing-lg)', 
            paddingTop: 'var(--spacing-lg)', 
            borderTop: '1px solid var(--border-primary)' 
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              marginBottom: 'var(--spacing-md)'
            }}>
              <div className="section-title" style={{ margin: 0 }}>
                <SettingsIcon size={14} color="#60D2CC" />
                <span>Parameters</span>
              </div>
              <button
                className="btn btn-sm btn-success"
                onClick={addParameter}
              >
                + Add
              </button>
            </div>

            {parameters.length === 0 ? (
              <div className="empty-state" style={{ padding: 'var(--spacing-lg)' }}>
                <div className="empty-state-text">No parameters. Click "Add" to create one.</div>
              </div>
            ) : (
              parameters.map((param, index) => (
                <ParameterEditor
                  key={index}
                  parameter={param}
                  onUpdate={(field, value) => updateParameter(index, field, value)}
                  onDelete={() => deleteParameter(index)}
                />
              ))
            )}

            <div className="alert alert-info" style={{ marginTop: 'var(--spacing-md)', display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
              <LightbulbIcon size={14} color="#60D2CC" style={{ marginTop: '2px', flexShrink: 0 }} />
              <span>Parameters defined here will be available as function arguments in your code</span>
            </div>
          </div>

          {/* Code Section */}
          <div style={{ 
            marginTop: 'var(--spacing-lg)', 
            paddingTop: 'var(--spacing-lg)', 
            borderTop: '1px solid var(--border-primary)' 
          }}>
            <div className="input-group">
              <label className="input-label">Implementation Code *</label>
              <textarea
                className="input input-mono"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                rows={10}
                placeholder="# Your Python code here
result = 'Hello, World!'
return {'status': 'success', 'result': result}"
              />
              <div className="input-hint">
                Write Python code. Parameters are available as variables. Use <code>return</code> to return results.
              </div>
            </div>

            <div className="alert alert-warning" style={{ marginTop: 'var(--spacing-md)' }}>
              ⚠️ <strong>Security Note:</strong> Code is executed on the backend. Only use trusted code.
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            {tool ? 'Save Changes' : 'Create Tool'}
          </button>
        </div>
      </div>
    </div>
  );
}

function ParameterEditor({ parameter, onUpdate, onDelete }) {
  return (
    <div className="card" style={{ marginBottom: 'var(--spacing-sm)' }}>
      <div className="card-body">
        <div style={{ display: 'flex', gap: 'var(--spacing-sm)', marginBottom: 'var(--spacing-sm)' }}>
          <input
            type="text"
            className="input font-mono"
            value={parameter.name}
            onChange={(e) => onUpdate('name', e.target.value)}
            placeholder="parameter_name"
            style={{ flex: 1 }}
          />
          <CustomDropdown
            value={parameter.type}
            options={[
              { value: 'string', label: 'string' },
              { value: 'integer', label: 'integer' },
              { value: 'number', label: 'number' },
              { value: 'boolean', label: 'boolean' },
              { value: 'array', label: 'array' },
              { value: 'object', label: 'object' }
            ]}
            onChange={(value) => onUpdate('type', value)}
            style={{ width: '120px' }}
          />
          <label className="checkbox-label" style={{ whiteSpace: 'nowrap' }}>
            <input
              type="checkbox"
              checked={parameter.required}
              onChange={(e) => onUpdate('required', e.target.checked)}
            />
            Required
          </label>
          <button
            className="btn btn-sm btn-danger"
            onClick={onDelete}
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
          >
            <TrashIcon size={14} />
          </button>
        </div>
        <input
          type="text"
          className="input"
          value={parameter.description}
          onChange={(e) => onUpdate('description', e.target.value)}
          placeholder="Parameter description..."
          style={{ fontSize: '12px' }}
        />
      </div>
    </div>
  );
}

export default ToolEditorDialog;
